import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, FormControlLabel, Switch } from '@mui/material';

import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import {
    ICertificateFragment,
    IScoringFragment,
    IOfferEventSubscriptionStatus,
} from 'graphql/types';

export type TEnrollmentStatusDialogSettings = {
    open: boolean;
    selectedAmount: number;
    enrollmentId?: string;
    status?: IOfferEventSubscriptionStatus;
    certificate?: ICertificateFragment;
    scoring?: IScoringFragment[];
};

interface IProps {
    dialogSettings: TEnrollmentStatusDialogSettings;
    loading?: boolean;
    certificate?: ICertificateFragment;
    scoring?: IScoringFragment[];
    showCertificateField?: boolean;
    onClose?(): void;
    onConfirm?(reason: string, generateCertificate?: boolean): void;
}

export const EnrollmentStatusDialog = ({
    dialogSettings,
    loading,
    onClose,
    onConfirm,
    showCertificateField = false,
}: IProps) => {
    const [translate] = useTranslation();
    const [reasonValue, setReasonValue] = useState('');
    const [generateCertificate, setGenerateCertificate] = useState(false);

    const { selectedAmount, open, status } = dialogSettings;

    useEffect(() => {
        if (open) return;

        // Reset value when dialog is closed
        setReasonValue('');
        setGenerateCertificate(false);
    }, [open]);

    if (!open) return null;

    const count = !selectedAmount ? 1 : selectedAmount;

    let transKey = '';

    let showReasonField = false;

    switch (status) {
        case IOfferEventSubscriptionStatus.Enrolled:
            transKey = 'enroll';
            break;
        case IOfferEventSubscriptionStatus.Denied:
            transKey = 'reject';
            showReasonField = true;
            break;
    }

    transKey = `offerEventEnrollmentStatusDialog.${transKey}`;

    const title = translate(`${transKey}.title`, { count });
    const text = translate(`${transKey}.text`, { count });
    const btnText = translate(`${transKey}.btnText`);

    return (
        <AlertDialog
            actions={
                <Box sx={{ display: 'flex', mt: 2 }}>
                    <Box sx={{ mr: 2 }}>
                        <Button
                            color="primary"
                            loading={loading}
                            variant="contained"
                            onClick={() =>
                                onConfirm?.(reasonValue, generateCertificate)
                            }
                        >
                            {btnText}
                        </Button>
                    </Box>

                    <Button
                        disabled={loading}
                        variant="contained"
                        onClick={() => onClose?.()}
                    >
                        {translate('close')}
                    </Button>
                </Box>
            }
            open={open}
            title={title}
        >
            {text}
            {showReasonField && (
                <TextField
                    fullWidth
                    multiline
                    disabled={loading}
                    label={translate(
                        'offerEventEnrollmentStatusDialog.reasonFieldLabel'
                    )}
                    margin="normal"
                    sx={{ textarea: { minHeight: '60px' } }}
                    value={reasonValue}
                    variant="outlined"
                    onChange={(e) => setReasonValue(e.currentTarget.value)}
                />
            )}
            {showCertificateField && (
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={generateCertificate}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => setGenerateCertificate(e.target.checked)}
                            />
                        }
                        label={translate(
                            'offerEventEnrollmentStatusDialog.certificateFieldLabel'
                        )}
                        sx={{
                            '.MuiFormControlLabel-label': {
                                fontWeight: 700,
                            },
                        }}
                    />
                </Box>
            )}
        </AlertDialog>
    );
};
